import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { inlineMediaUrl } from '../../inlineMediaUrl';
import { SerializedReel } from '../../types';
import { isReelWorking } from './reelStages';
import { reelReviewMessages, reelStatusMessage } from './reelReviewMessages';
import { useReelHistoryWhy } from './useReelHistoryWhy';
import { ReelStatusPill } from './ReelStatusPill';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelHistoryCardProps {
  reel: SerializedReel;
  isCurrent: boolean;
  onOpen: (uuid: string) => void;
}

/** One reel in the history strip (design c1). A click opens that reel. */
const ReelHistoryCardComponent: React.FC<ReelHistoryCardProps> = ({ reel, isCurrent, onOpen }) => {
  const intl = useIntl();
  const why = useReelHistoryWhy(reel);
  const isWorking = isReelWorking(reel.status);
  const posterUrl = reel.video?.posterUrl ?? null;
  const date = isWorking
    ? intl.formatMessage(reelReviewMessages.historyNow)
    : intl.formatDate(reel.createdAt, { day: 'numeric', month: 'short' });

  return (
    <li>
      <button
        type="button"
        className={clsx(styles.hcard, isCurrent && styles.hcardCurrent)}
        aria-current={isCurrent ? 'true' : undefined}
        aria-label={intl.formatMessage(reelReviewMessages.historyOpen, {
          date,
          status: intl.formatMessage(reelStatusMessage(reel.status)),
          why,
        })}
        onClick={() => onOpen(reel.uuid)}
      >
        <span className={clsx(styles.hposter, reel.status === 'rejected' && styles.hposterDim)}>
          {posterUrl !== null && <img className={styles.hposterImage} src={inlineMediaUrl(posterUrl)} alt="" />}
          {isWorking && <span className={styles.hposterShimmer} />}
          {reel.status === 'failed' && (
            <span className={styles.hposterMid}>
              <ReelStatusPill status="failed" />
            </span>
          )}
        </span>
        <span className={styles.hrow}>
          <ReelStatusPill status={reel.status} />
          <span className={styles.hdate}>{date}</span>
        </span>
        <span className={styles.why}>{why}</span>
      </button>
    </li>
  );
};

export const ReelHistoryCard = TypedMemo(ReelHistoryCardComponent);
